import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { SerializedSmartLink } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../common/types';
import { SMLPreviewMessageData, SMLPreviewMessages } from '../smartLinkDetailsView/messageListeners/types';
import { SMLEditorContext, SMLEditorContextValue } from './SMLEditorContext';
import { useEditorUrlState } from './url/useEditorUrlState';
import { editorStatus } from './topBar/editorStatus';
import { useEditorPublish } from './topBar/useEditorPublish';
import { SMLEditorTopBar } from './topBar/SMLEditorTopBar';
import { SMLEditorPanel } from './panel/SMLEditorPanel';
import { SMLEditorStage } from './SMLEditorStage';
import { SMLEditorPhoneBar } from './SMLEditorPhoneBar';
import { SMLEditorPhonePreview } from './SMLEditorPhonePreview';
import { SMLEditorOverlays } from './SMLEditorOverlays';
import { useRequestDelete } from './blocks/useRequestDelete';
import { EditorSaveTracker } from './saves/editorSaveTracker';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

const NOTICE_MS = 5000;

interface SMLEditorProps {
  smartLink: SerializedSmartLink;
  name: string;
  slugPrefix: string;
  artistName: string | null;
  onBack: () => void;
  onOpenPage: () => void;
  /** The first publish opens the published sheet, a later one a toast. */
  onPublished: (isFirstPublish: boolean) => void;
}

/**
 * The SmartLink editor (design smartlink-editor a1–a8): the top bar, the preview and the panel with
 * Blocks, Look and Page. Every change saves to the draft; "Publish changes" makes it live.
 */
const SMLEditorComponent: React.FC<SMLEditorProps> = ({
  smartLink,
  name,
  slugPrefix,
  artistName,
  onBack,
  onOpenPage,
  onPublished,
}) => {
  const url = useEditorUrlState();
  const isCompact = useMediaQuery({ query: '(max-width: 991px)' });
  const [hoveredUUID, setHovered] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // One tracker for the life of the editor: every save of the draft goes through it.
  const [saves] = useState(() => new EditorSaveTracker());
  const { accountId, id: smartLinkId, isMainSmartLink: isBioLink } = smartLink;
  const { openBlockUUID, closeLayer } = url;
  const onDeleted = useCallback((uuid: string) => uuid === openBlockUUID && closeLayer(), [closeLayer, openBlockUUID]);
  const removal = useRequestDelete({ accountId, smartLinkId, artistName, onDeleted, showNotice: setNotice, saves });
  const { isGone } = removal.deletes;
  const publish = useEditorPublish({ smartLink, onPublished, saves });
  const status = editorStatus(smartLink);

  useEffect(() => {
    if (notice === null) {
      return undefined;
    }
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const pageWidgets = useMemo(
    () => [...(smartLink.recentSmartLinkVersion.widgets ?? [])].sort((a, b) => a.position - b.position),
    [smartLink.recentSmartLinkVersion.widgets]
  );
  const widgets = useMemo(() => pageWidgets.filter((widget) => !isGone(widget.uuid)), [isGone, pageWidgets]);
  // The preview draws the draft without a row whose delete waits for the toast.
  const previewSmartLink = useMemo(
    () => ({ ...smartLink, recentSmartLinkVersion: { ...smartLink.recentSmartLinkVersion, widgets } }),
    [smartLink, widgets]
  );

  const onPreviewMessage = useCallback(
    (data: SMLPreviewMessageData) => {
      if (data.type === SMLPreviewMessages.edit && data.widgetUUID !== null) {
        url.openBlock(data.widgetUUID);
      } else if (data.type === SMLPreviewMessages.hover) {
        setHovered(data.widgetUUID);
      }
    },
    [url]
  );
  const openPageTab = useCallback(() => url.setTab('page'), [url]);

  const context = useMemo<SMLEditorContextValue>(
    () => ({
      smartLink,
      accountId,
      smartLinkId,
      isBioLink,
      artistName,
      slugPrefix,
      url,
      hoveredUUID,
      setHovered,
      requestDelete: removal.requestDelete,
      showNotice: setNotice,
      isCompact,
      saves,
    }),
    [
      accountId,
      artistName,
      hoveredUUID,
      isBioLink,
      isCompact,
      removal.requestDelete,
      saves,
      slugPrefix,
      smartLink,
      smartLinkId,
      url,
    ]
  );
  const address = isBioLink ? slugPrefix.replace(/\/$/, '') : `${slugPrefix}${smartLink.recentSmartLinkVersion.slug}`;

  return (
    <SMLEditorContext.Provider value={context}>
      <div className={styles.editor}>
        <SMLEditorTopBar
          accountId={accountId}
          name={name}
          isBioLink={isBioLink}
          address={address}
          status={status}
          blockCount={widgets.length}
          publish={publish}
          onBack={onBack}
          onOpenPageTab={openPageTab}
          onOpenPage={onOpenPage}
          isCompact={isCompact}
        />
        <div className={styles.body}>
          {!isCompact && (
            <SMLEditorStage
              smartLink={previewSmartLink}
              selectedWidgetUUID={openBlockUUID}
              hoveredWidgetUUID={hoveredUUID}
              onPreviewMessage={onPreviewMessage}
            />
          )}
          <SMLEditorPanel widgets={widgets} pageWidgets={pageWidgets} status={status} publish={publish} />
        </div>
        {isCompact && (
          <SMLEditorPhoneBar
            accountId={accountId}
            status={status}
            blockCount={widgets.length}
            publish={publish}
            onPreview={url.openPreview}
          />
        )}
        {isCompact && url.isPreviewOpen && (
          <SMLEditorPhonePreview
            smartLink={previewSmartLink}
            status={status}
            onEdit={closeLayer}
            onPreviewMessage={onPreviewMessage}
          />
        )}
        <SMLEditorOverlays removal={removal} notice={notice} onCloseNotice={setNotice} />
      </div>
    </SMLEditorContext.Provider>
  );
};

export const SMLEditor = TypedMemo(SMLEditorComponent);
